import { useLocale, useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import { Paper, Title, Loader, Stack, Text, Tabs, SimpleGrid, Box, Button, Divider } from "@mantine/core";
import { colorSeasons, Color } from "@/constants";
import { ColorCard } from "@/app/[locale]/color-palette/[season]/[subSeason]/content";
import ImageGallery from "@/app/[locale]/components/ImageGallery";

interface ColorAnalysisResultsProps {
  colorAnalysis: any;
  loading: boolean;
  analysisId?: string;
}

interface MakeupPalette {
  name: string;
  colors: Color[];
}

export default function ColorAnalysisResults({ colorAnalysis, loading, analysisId }: ColorAnalysisResultsProps) {
  const t = useTranslations("kibbeFlow.color"), locale = useLocale();
  if (loading || !colorAnalysis) {
    return (
      <Paper shadow="sm" p="xl" radius="md" withBorder mt="xl">
        <Title order={2} mb="md" ta="center">
          {t("title")}
        </Title>
        <Stack align="center" gap="md">
          <Loader size="lg" />
          <Text c="dimmed" ta="center">
            {t("preparing")}
          </Text>
        </Stack>
      </Paper>
    );
  }

  if (!colorAnalysis?.results) {
    return (
      <Paper shadow="sm" p="xl" radius="md" withBorder mt="xl">
        <Title order={2} mb="md" ta="center">
          {t("title")}
        </Title>
        <Text c="dimmed" ta="center">
          {t("missing")}
        </Text>
      </Paper>
    );
  }

  const { season, subSeason, explanation, makeupRecommendations } = colorAnalysis.results;
  const seasonData = colorSeasons[season?.toUpperCase()];
  const subSeasonData = seasonData?.subseasons?.[subSeason?.toUpperCase()];
  const emoji = subSeasonData?.emoji || seasonData?.emoji || "";
  const description = subSeasonData?.description || "";

  return (
    <Paper shadow="sm" p="xl" radius="md" withBorder mt="xl">
      <Title order={4} c="dimmed" ta="center">
        {t("season")}
      </Title>
      <Title order={2} fz={{ base: "h3", md: "h1" }} mb="md" ta="center">
        {emoji} {subSeason.charAt(0) + subSeason.slice(1).toLowerCase()}{" "}
        {season.charAt(0) + season.slice(1).toLowerCase()} {emoji}
      </Title>
      <Text mb="xs" c="dimmed" ta="left">
        {explanation}
      </Text>
      <Text mb="xl" c="dimmed" ta="left">
        {locale === "en" ? description : ""}
      </Text>

      <Tabs defaultValue="colors" variant="outline" color="wumpus" w="100%" mb="0">
        <Tabs.List>
          <Tabs.Tab value="colors">{t("colors")}</Tabs.Tab>
          <Tabs.Tab value="makeup">{t("makeup")}</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="colors">
          <Paper
            withBorder
            p="md"
            radius="md"
            mb="md"
            mt="0"
            style={{ borderTop: "none", borderTopLeftRadius: "0", borderTopRightRadius: "0" }}
          >
            <section>
              <Title order={4} mb="sm" c="dimmed" pt="sm">
                {t("primary")}
              </Title>
              <SimpleGrid cols={{ base: 3, md: 6 }} spacing="sm">
                {subSeasonData?.primaryColors?.map((color: Color) => (
                  <ColorCard key={color.key} color={color} />
                ))}
              </SimpleGrid>
            </section>
            <section>
              <Title order={4} my="sm" c="dimmed">
                {t("complementary")}
              </Title>
              <SimpleGrid cols={{ base: 2, md: 4 }} spacing="sm">
                {subSeasonData?.complementaryColors?.map((color: Color) => (
                  <ColorCard key={color.key} color={color} />
                ))}
              </SimpleGrid>
            </section>
            <section>
              <Title order={4} my="sm" c="dimmed">
                {t("neutral")}
              </Title>
              <SimpleGrid cols={{ base: 2, md: 4 }} spacing="sm">
                {subSeasonData?.neutrals?.map((color: Color) => (
                  <ColorCard key={color.key} color={color} />
                ))}
              </SimpleGrid>
            </section>
          </Paper>
        </Tabs.Panel>
        <Tabs.Panel value="makeup">
          <Paper
            withBorder
            p="md"
            radius="md"
            mb="md"
            mt="0"
            style={{ borderTop: "none", borderTopLeftRadius: "0", borderTopRightRadius: "0" }}
          >
            <Stack gap="lg">
              {makeupRecommendations?.blush && (
                <Box>
                  <Title order={5} mb="sm" c="dimmed">
                    {t("blush")}
                  </Title>
                  <Text c="dimmed" ta="center" mb="sm" fz="sm">
                    {makeupRecommendations.blush.explanation}
                  </Text>
                  <SimpleGrid cols={{ base: 2, md: 4 }} spacing="sm">
                    {makeupRecommendations.blush.recommendedColors?.map((color: Color) => (
                      <ColorCard key={color.key} color={color} />
                    ))}
                  </SimpleGrid>
                </Box>
              )}
              {makeupRecommendations?.lipstick && (
                <Box>
                  <Divider my="sm" />
                  <Title order={5} mb="sm" c="dimmed">
                    {t("lipstick")}
                  </Title>
                  <Text c="dimmed" ta="center" mb="sm" fz="sm">
                    {makeupRecommendations.lipstick.explanation}
                  </Text>
                  <SimpleGrid cols={{ base: 2, md: 4 }} spacing="sm">
                    {makeupRecommendations.lipstick.recommendedColors?.map((color: Color) => (
                      <ColorCard key={color.key} color={color} />
                    ))}
                  </SimpleGrid>
                </Box>
              )}
              {makeupRecommendations?.eyeshadow && (
                <Box>
                  <Divider my="sm" />
                  <Title order={5} mb="sm" c="dimmed">
                    {t("eyeshadow")}
                  </Title>
                  <Text c="dimmed" ta="center" mb="sm" fz="sm">
                    {makeupRecommendations.eyeshadow.explanation}
                  </Text>
                  {makeupRecommendations.eyeshadow.recommendedPalettes?.map((palette: MakeupPalette) => (
                    <Box key={palette.name} mb="md">
                      <Title order={6} mb="xs" c="dimmed" ta="center">
                        {palette.name}
                      </Title>
                      <SimpleGrid cols={{ base: 2, md: 4 }} spacing="sm">
                        {palette.colors.map((color: Color) => (
                          <ColorCard key={color.key} color={color} />
                        ))}
                      </SimpleGrid>
                    </Box>
                  ))}
                </Box>
              )}
            </Stack>
          </Paper>
        </Tabs.Panel>
      </Tabs>

      {/* Processed Outputs Gallery */}
      {colorAnalysis.processedOutputs && colorAnalysis.processedOutputs.length > 0 && (
        <Box mb="xl" mt="xl">
          <Title order={4} mb="sm" c="dimmed">
            {t("gallery")}
          </Title>
          <Text color="dimmed" mb="md">
            {t("galleryNote")}
          </Text>
          <ImageGallery
            paths={
              colorAnalysis.processedOutputs?.map(
                (output: { outfitName: string; outputPath: string; outfitType?: string }) => ({
                  outfitName: output.outfitName || t("outfit"),
                  outputPath: output.outputPath,
                  outfitType: output.outfitType,
                })
              ) || []
            }
            analysisId={analysisId}
          />
        </Box>
      )}

      {/* Full color-analysis report (the rich standalone viewer) */}
      {analysisId && (
        <Button
          component={Link}
          color="wumpus"
          variant="filled"
          href={`/analysis-wizard?analysisId=${analysisId}`}
          mt="xl"
          fullWidth
        >
          {t("fullReport")}
        </Button>
      )}

      {/* Learn More Button */}
      <Button
        component={Link}
        color="wumpus"
        variant="light"
        href={`/color-palette/${season.toLowerCase()}/${subSeason.toLowerCase().replace("_", "-")}`}
        mt="md"
        fullWidth
      >
        {t("learnMore")}
      </Button>
    </Paper>
  );
}
